import { isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';

import { Block } from '@/components/block';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { AdSlot } from '@/components/ui/adslot';
import { ADS_AIR_ROUTE_SLOT, ADS_OCEAN_ROUTE_SLOT, MODE_AIR, MODE_ROAD, MODE_WATER } from '@/lib/constants';
import { RouteSummaryGroupModel } from '@/lib/models/route-summary/types';
import { featuresState } from '@/state/features';
import { LocationInterface } from '@/types';
import { getModeDescriptionString, getRoadDescriptionString } from '@/utils/strings';
import { tv } from '@/utils/styles';

import { PageRouteCard } from './card';
import { PageRouteCta } from './cta';
import { PageRouteSidebar } from './sidebar';

export interface PageRoutesProps {
  from: LocationInterface;
  to: LocationInterface;
  routes: RouteSummaryGroupModel;
}

const PageRoutes = ({ from, to, routes }: PageRoutesProps) => {
  const features = useRecoilValue(featuresState);

  const fromToNameString = `${from.name} to ${to.name}`;
  const airRoutes = routes[MODE_AIR];
  const oceanRoutes = routes[MODE_WATER];
  const roadRoutes = routes[MODE_ROAD];

  // Route Mode Descriptions
  const airDescription = getModeDescriptionString(airRoutes, MODE_AIR, from, to);
  const oceanDescription = getModeDescriptionString(oceanRoutes, MODE_WATER, from, to);
  const roadDescription = getRoadDescriptionString(roadRoutes, from, to);

  const ADSLOT_ENABLED = features.showAds;
  const shouldShowAirAd = ADSLOT_ENABLED && !isEmpty(airRoutes);
  const shouldShowOceanAd = ADSLOT_ENABLED && oceanRoutes?.length > 1;

  const { layout, cards } = styles();

  return (
    <div>
      <Block id="routes">
        <div className={layout()}>
          <div>
            <PageRouteSidebar
              from={from}
              to={to}
              airRoutes={airRoutes}
              oceanRoutes={oceanRoutes}
              roadRoutes={roadRoutes}
            />
          </div>
          <div>
            {!isEmpty(airRoutes) && (
              <div id="air-routes">
                <Heading variant="h2" className="mb-base">
                  {fromToNameString} by Air freight
                </Heading>
                <Text className="mb-12">{airDescription}</Text>
                <div className={cards()}>
                  {airRoutes.map((routeCard) => (
                    <PageRouteCard key={routeCard.id} from={from} to={to} route={routeCard} />
                  ))}
                </div>
                <PageRouteCta mode={MODE_AIR} from={from} to={to} route={airRoutes[0]} />
              </div>
            )}
            {shouldShowAirAd && <AdSlot slotId={ADS_AIR_ROUTE_SLOT} format="horizontal,rectangle" />}
            {!isEmpty(oceanRoutes) && (
              <div id="ocean-routes" className="mt-20">
                <Heading variant="h2" className="mb-base">
                  {fromToNameString} by Container ship
                </Heading>
                <Text className="mb-12">{oceanDescription}</Text>
                <div className={cards()}>
                  {oceanRoutes.map((routeCard) => (
                    <PageRouteCard key={routeCard.id} from={from} to={to} route={routeCard} />
                  ))}
                </div>
                <PageRouteCta mode={MODE_WATER} from={from} to={to} route={oceanRoutes[0]} />
              </div>
            )}
            {shouldShowOceanAd && <AdSlot slotId={ADS_OCEAN_ROUTE_SLOT} format="horizontal,rectangle" />}
            {!isEmpty(roadRoutes) && (
              <div id="ocean-routes" className="mt-20">
                <Heading variant="h2" className="mb-base">
                  {fromToNameString} by Road
                </Heading>
                <Text className="mb-12">{roadDescription}</Text>
                {roadRoutes.map((routeCard) => (
                  <PageRouteCard key={routeCard.id} from={from} to={to} route={routeCard} />
                ))}
              </div>
            )}
          </div>
        </div>
      </Block>
    </div>
  );
};

const styles = tv({
  slots: {
    layout: 'grid gap-16 lg:grid-cols-[260px_1fr] lg:gap-12 xl:grid-cols-[340px_1fr]',
    cards: 'flex flex-col gap-12',
  },
});

export { PageRoutes };
